import type { AgenticLogicRuleDto } from "@intelligems/ig-types/dtos/agentic-logic/rules";
import { useAgenticLogicRuleVersions } from "@src/api/agentic-logic/get-agentic-logic-rule-versions";
import { useAgenticLogicRuleDraft } from "@src/api/agentic-logic/get-agentic-logic-rule-draft";
import { agenticLogicDraftMatchesVersion } from "@intelligems/ig-types/functions/agentic-logic/workspace/draft-matches-version";
import {
  hasUnpublishedRuleConfig,
  hasUnpublishedRuleSettings,
} from "./unpublishedSettings";
import type { AgenticLogicRuleVersionDto } from "@intelligems/ig-types/dtos/agentic-logic/versions";

/** Compare the saved draft with the published version. Local edits are tracked by the editor. */
export const useUnpublishedRuleChanges = (
  rule: AgenticLogicRuleDto
): {
  hasUnpublishedChanges: boolean;
  isChecking: boolean;
  comparisonFailed: boolean;
  activeVersion: AgenticLogicRuleVersionDto | null;
  hasSavedWork: boolean;
  retry: () => void;
} => {
  const versionsQuery = useAgenticLogicRuleVersions(rule.id);
  const activeVersion =
    versionsQuery.data?.versions.find(
      (version) => version.status === "active"
    ) ?? null;
  // Atlas can author code too; the template binding identifies the runtime.
  const isCode = rule.templateId == null;
  const draftQuery = useAgenticLogicRuleDraft(isCode ? rule.id : undefined);
  const draft = draftQuery.data?.draft ?? null;
  // The same fingerprint answer the rules list gives, so the two never
  // disagree about whether the saved code has been applied.
  const codeChanged =
    isCode &&
    activeVersion !== null &&
    draftQuery.data !== undefined &&
    !agenticLogicDraftMatchesVersion({
      draft: draft && {
        hasSource: draft.source !== null,
        contentFingerprint: draft.contentFingerprint,
      },
      versionFingerprint: activeVersion.contentFingerprint,
    });

  return {
    activeVersion,
    hasSavedWork:
      !isCode || draftQuery.data?.draft != null || activeVersion != null,
    retry: () => {
      void versionsQuery.refetch();
      if (isCode) {
        void draftQuery.refetch();
      }
    },
    isChecking: versionsQuery.isLoading || (isCode && draftQuery.isLoading),
    comparisonFailed: versionsQuery.isError || (isCode && draftQuery.isError),
    hasUnpublishedChanges:
      hasUnpublishedRuleSettings(
        rule.settings,
        activeVersion?.settings ?? null
      ) ||
      hasUnpublishedRuleConfig(
        rule.templateParams,
        activeVersion?.templateParams ?? null
      ) ||
      codeChanged,
  };
};
